import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { ITaskComment, ITask } from '../../interfaces/ITasks';
import { useApiClient } from '../../ApiClientProvider';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchUsers } from '../../redux/usersSlice';
import { useDialogs, useNotifications } from '@toolpad/core';
import { Dialog, DialogTitle, Box, Typography, IconButton, DialogContent, Grid, Paper, Chip, TextField, MenuItem, Avatar, CircularProgress, DialogActions, Button } from '@mui/material';
import { Assignment, Delete, Comment, Send } from '@mui/icons-material';
import { EventTaskStatus } from '../../enums/EventTaskStatus';
import { EventService } from '../../services/eventService';
import { MentionTextField, MentionValue } from '../common/MentionTextField';

interface TaskDetailsModalProps {
    eventId: string;
    task: ITask;
    onClose: () => void;
    onUpdate: (task: ITask) => void;
    onDelete: (taskId: string) => void;
}

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
    eventId,
    task,
    onClose,
    onUpdate,
    onDelete,
}) => {
    const [title, setTitle] = useState(task.title);
    const [description, setDescription] = useState(task.description);
    const [status, setStatus] = useState<EventTaskStatus>(task.status);
    const [assignedTo, setAssignedTo] = useState(task.assignedTo);
    const [dueDate, setDueDate] = useState<Dayjs | null>(dayjs(task.dueDate));
    const [newComment, setNewComment] = useState('');
    const [descriptionMentionedUserIds, setDescriptionMentionedUserIds] = useState<string[]>(task.mentionedUserIds ?? []);
    const [commentMentions, setCommentMentions] = useState<MentionValue[]>([]);
    const [comments, setComments] = useState<ITaskComment[]>([]);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [isPostingComment, setIsPostingComment] = useState(false);
    const [commentsError, setCommentsError] = useState<string | null>(null);
    const apiClient = useApiClient('applicationClient');
    const apiClientforUser = useApiClient('managementClient');
    const eventService = new EventService(apiClient);
    const dispatch = useDispatch<AppDispatch>();
    const { users } = useSelector((state: RootState) => state.users);
    const notifications = useNotifications();
    const dialogs = useDialogs();

    const mentionCandidates = users.flatMap((user) => {
        const userId = user.userId ?? user.id;
        if (!userId) return [];
        const displayName = `${user.firstName} ${user.lastName}`.trim() || user.email;
        return [{ id: userId, displayName, imageUrl: user.imageUrl }];
    });

    const statusColors: Record<EventTaskStatus, string> = {
        [EventTaskStatus.New]: '#2196f3',
        [EventTaskStatus.Active]: '#ff9800',
        [EventTaskStatus.Closed]: '#4caf50',
    };

    const statusLabels: Record<EventTaskStatus, string> = {
        [EventTaskStatus.New]: 'New',
        [EventTaskStatus.Active]: 'Active',
        [EventTaskStatus.Closed]: 'Closed',
    };

    const statusConfig = {
        New: { label: 'New', color: '#2196f3' },
        Active: { label: 'Active', color: '#ff9800' },
        Closed: { label: 'Closed', color: '#4caf50' },
    };

    useEffect(() => {
        const fetchComments = async () => {
            setIsLoadingComments(true);
            setCommentsError(null);
            try {
                const response = await eventService.GetTaskComments(eventId, task?.id ?? '');
                setComments(response || []);
            } catch {
                setCommentsError('Failed to load comments');
            } finally {
                setIsLoadingComments(false);
            }
        };
        fetchComments();
    }, [task.id]);

    useEffect(() => {
        const fetchData = async () => {
            await dispatch(fetchUsers(apiClientforUser));
        }
        fetchData();
    }, []);


    const handleUpdate = async () => {
        if (!title || !assignedTo || !dueDate) return;

        const currentMentionedUserIds = descriptionMentionedUserIds;

        const updatedTask: ITask = {
            ...task,
            title,
            description,
            status,
            assignedTo,
            dueDate: dueDate.toDate(),
            mentionedUserIds: currentMentionedUserIds,
        };
        const result = await eventService.UpdateTask(eventId, task?.id ?? '', updatedTask);
        onUpdate({ ...result, mentionedUserIds: currentMentionedUserIds });
        onClose();
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        setIsPostingComment(true);

        const tempComment: ITaskComment = {
            content: newComment.trim(),
            mentionedUserIds: commentMentions.map((mention) => mention.userId),
        };

        try {
            const response = await eventService.AddTaskComment(eventId, task?.id ?? '', tempComment);
            if (response) {
                setComments(prevComments => [...prevComments, response]);
                setNewComment('');
                setCommentMentions([]);
            }
        } catch {
            setNewComment(tempComment.content);
            notifications.show("Error while adding a comment please try again.", {
                autoHideDuration: 3000,
                severity: 'error'
            })
        } finally {
            setIsPostingComment(false);
        }
    };

    const handleDeleteWithConfirmation = async () => {
        const result = await dialogs.confirm('Are you sure you want to delete this task? This action cannot be undone.', {
            okText: 'Yes',
            cancelText: 'No'
        })
        if (result) {
            onDelete(task.id ?? '');
        }
    };

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2,
                    }
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Assignment color="primary" />
                        <Typography variant="h5" fontWeight="600">
                            Task Details
                        </Typography>
                        <Chip
                            label={statusLabels[status]}
                            size="small"
                            sx={{
                                backgroundColor: statusColors[status],
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 20,
                            }}
                        />
                    </Box>
                    <IconButton
                        onClick={handleDeleteWithConfirmation}
                        size="small"
                        color="error"
                    >
                        <Delete />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Grid container sx={{ height: '60vh' }}>
                    {/* Left Panel - Task Information */}
                    <Grid size={{ xs: 12, md: 7 }} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider', pr: { md: 2 } }}>
                        <Box sx={{ p: 4 }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Created {dayjs(task.createdAt).format('MMM DD, YYYY')}
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        label="Task Title"
                                        fullWidth
                                        size="small"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <MentionTextField
                                        label="Description"
                                        multiline
                                        rows={4}
                                        value={description}
                                        mentions={descriptionMentionedUserIds.flatMap((userId) => {
                                            const user = users.find((candidate) => (candidate.userId ?? candidate.id) === userId);
                                            if (!user) return [];
                                            return [{ userId, displayName: `${user.firstName} ${user.lastName}`.trim() || user.email }];
                                        })}
                                        candidates={mentionCandidates}
                                        onChange={(value, mentions) => {
                                            setDescription(value);
                                            setDescriptionMentionedUserIds(mentions.map((mention) => mention.userId));
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }} >
                                    <TextField
                                        label="Status"
                                        select
                                        fullWidth
                                        size="small"
                                        value={status}
                                        onChange={(e) => setStatus(Number(e.target.value) as EventTaskStatus)}
                                    >
                                        {Object.entries(statusConfig).map(([key, config]) => (
                                            <MenuItem key={key} value={EventTaskStatus[key as keyof typeof EventTaskStatus]}>
                                                <Chip
                                                    label={config.label}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: config.color,
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.7rem',
                                                        height: 20,
                                                    }}
                                                />
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 8 }}>
                                    <TextField
                                        label="Assigned To"
                                        select
                                        fullWidth
                                        size="small"
                                        value={assignedTo}
                                        onChange={(e) => setAssignedTo(e.target.value)}
                                    >
                                        {users.length === 0 ? (
                                            <MenuItem disabled>
                                                <Typography color="text.secondary">Loading users...</Typography>
                                            </MenuItem>
                                        ) : (
                                            users.map((user) => (
                                                <MenuItem key={user.id} value={user.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }} src={user.imageUrl}>
                                                            {user.firstName?.charAt(0) || user.email?.charAt(0)}
                                                        </Avatar>
                                                        {user.firstName} {user.lastName}
                                                    </Box>
                                                </MenuItem>
                                            ))
                                        )}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <DatePicker
                                        label="Due Date"
                                        value={dueDate}
                                        onChange={setDueDate}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small'
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    {/* Right Panel - Comments */}
                    <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%', pl: { md: 2 } }}>
                        <Box sx={{ pb: 1, mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Comment color="primary" />
                                <Typography variant="subtitle1" fontWeight="600">
                                    Discussion ({comments.length})
                                </Typography>
                                {isLoadingComments && (
                                    <CircularProgress size={16} sx={{ ml: 1 }} />
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Collaborate with your team
                            </Typography>
                            {commentsError && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                    {commentsError}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflow: 'auto',
                                    mb: 1,
                                }}
                            >
                                {isLoadingComments ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: 'text.secondary'
                                    }}>
                                        <CircularProgress size={32} sx={{ mb: 2 }} />
                                        <Typography variant="body2">
                                            Loading comments...
                                        </Typography>
                                    </Box>
                                ) : comments.length === 0 ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: 'text.secondary'
                                    }}>
                                        <Typography variant="caption" sx={{ textAlign: 'center' }}>
                                            No comments yet
                                        </Typography>
                                        <Typography variant="caption" sx={{ textAlign: 'center', mt: 1 }}>
                                            Start the conversation below
                                        </Typography>
                                    </Box>
                                ) : (
                                    comments.map((comment, index) => (
                                        <Paper
                                            key={comment.id || `comment-${index}`}
                                            variant="outlined"
                                            sx={{ p: 1.5, mb: 1.5,mr:2 }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <Avatar sx={{ width: 28, height: 28, mr: 2, fontSize: '0.875rem' }}>
                                                    {comment.author?.charAt(0) || 'U'}
                                                </Avatar>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="caption" fontWeight="600">
                                                            {(comment?.author ?? 'Unknown').toString()}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            {comment.timestamp ? dayjs(comment.timestamp).format('MMM DD, HH:mm') : 'Just now'}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                                                        {comment.content}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))
                                )}
                            </Box>

                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                    <MentionTextField
                                        label="Add a comment"
                                        multiline
                                        rows={1}
                                        value={newComment}
                                        mentions={commentMentions}
                                        candidates={mentionCandidates}
                                        onChange={(value, mentions) => {
                                            setNewComment(value);
                                            setCommentMentions(mentions);
                                        }}
                                        disabled={isPostingComment}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey && !isPostingComment) {
                                                e.preventDefault();
                                                handleAddComment();
                                            }
                                        }}
                                    />
                                    <IconButton
                                        color="primary"
                                        onClick={handleAddComment}
                                        disabled={!newComment.trim() || isPostingComment}
                                        size="small"
                                    >
                                        {isPostingComment ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <Send />
                                        )}
                                    </IconButton>
                                </Box>
                            </Paper>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 2,
                }}
            >
                <Button
                    onClick={onClose}
                    size="small"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleUpdate}
                    variant="contained"
                    size="small"
                    disabled={!title || !assignedTo || !dueDate}
                >
                    Update Task
                </Button>
            </DialogActions>
        </Dialog >
    );
};

export default TaskDetailsModal;